import config from "config/config"
import {
	HOME,
	LOGIN_LANDING,
	PINGFED_CALLBACK,
	ROOT_CONTEXT,
} from "constants/paths"
import {
	httpGetRequestWithCredentials,
	httpPostRequestWithCredentials,
} from "clients/phronesisClient"
import { redirect } from "navigation/redirect"
import { TERMS_AND_CONDITIONS } from "components/TermsAndConditions/constants"
import { MEDALLIA_LAST_UPDATE } from "constants/medallia"

interface AuthResponse {
	token: string
	email: string
	firstName: string
	lastName: string
	roles: Array<string>
	isSupplier: boolean
	lastAcceptedTerms: string
	plans: Array<string>
	organizationName: string
	medalliaLastUpdate: string
}

export type FeatureProperties = {
	[key in string]: Array<string>
}

export const LOGIN_ENDPOINT = "/login"
export const LOGOUT_ENDPOINT = "/logout"
export const SESSION_ENDPOINT = "/session"
const QS_STATE = "state"
const QS_AUTH_CODE = "code"
const KEY_VENDORS = "vendors_filter"

const pingFedClientId = config.pingfed_config.client_id
const pingFedLoginUrl = config.pingfed_config.login_url

const baseUrl = config.base_url

export const removeKeyIntoSessionStorage = (key: string) =>
	sessionStorage.removeItem(key)

export const redirectToPingFedSSO = (originPath: string) => {
	const queryParams = new URLSearchParams({
		response_type: "code",
		scope: "openid profile full",
		state: originPath,
		client_id: pingFedClientId,
		redirect_uri: `${baseUrl}${PINGFED_CALLBACK}`,
	})
	const url = `${pingFedLoginUrl}?${queryParams}`
	redirect(url)
}

export type authenticateResponse = {
	success: boolean
	originPath: string
	roles: Array<string>
}
// eslint-disable-next-line complexity
export const authenticate = async (
	currentPath: string,
	queryParams: Map<string, string>
): Promise<authenticateResponse> => {
	const originPath = queryParams.get(QS_STATE) || HOME
	const authCredentials = getAuthCredentials(currentPath, queryParams)
	const authenticate = await authenticateRequest(authCredentials)
	const { success, roles } = authenticate
	return { success, originPath, roles }
}

export const logoutWithoutLoginRedirection = async (
	terminateSession: boolean
) => {
	if (terminateSession) {
		await logoutRequest()
	}

	SessionDataStore.getInstance().clear()
}

export const logout = async (terminateSession: boolean, message?: string) => {
	removeKeyIntoSessionStorage(KEY_VENDORS)
	await logoutWithoutLoginRedirection(terminateSession)

	redirect(
		ROOT_CONTEXT +
			LOGIN_LANDING +
			(message === undefined ? "" : "?reason=" + message)
	)
}

const authenticateRequest = async (body: object) => {
	let authenticateData
	try {
		authenticateData = await httpPostRequestWithCredentials(
			LOGIN_ENDPOINT,
			body
		)
		localStorage.setItem(
			TERMS_AND_CONDITIONS,
			authenticateData.lastAcceptedTerms
		)
		localStorage.setItem(
			MEDALLIA_LAST_UPDATE,
			authenticateData.medalliaLastUpdate
		)
		SessionDataStore.getInstance().save(authenticateData)
	} catch {
		return { success: false, roles: [] }
	}
	return { success: true, roles: authenticateData.roles }
}

const logoutRequest = () => {
	return httpPostRequestWithCredentials(LOGOUT_ENDPOINT, undefined).catch(
		() => null
	)
}

function getAuthCredentials(
	currentPath: string,
	queryParams: Map<string, string>
) {
	return {
		login_provider: currentPath.split("-").slice(-1)[0],
		authorization_code: queryParams.get(QS_AUTH_CODE) || "",
		logout_token: "",
		redirect_uri: `${baseUrl}${currentPath}`,
	}
}

export const checkExistingSession = () => {
	return httpGetRequestWithCredentials<AuthResponse>(SESSION_ENDPOINT)
}

export class SessionDataStore {
	private static instance: SessionDataStore

	private token: string
	private email: string
	private firstName: string
	private lastName: string
	private roles: Array<string>
	private features: FeatureProperties
	private isSupplier: boolean
	private lastAcceptedTerms: string
	private medalliaLastUpdate: string
	private plans: Array<string>
	private organizationName: string

	private constructor() {
		this.token = ""
		this.email = ""
		this.firstName = ""
		this.lastName = ""
		this.roles = []
		this.features = {}
		this.isSupplier = false
		this.lastAcceptedTerms = ""
		this.plans = []
		this.organizationName = ""
		this.medalliaLastUpdate = ""
	}

	public static getInstance() {
		if (!SessionDataStore.instance) {
			SessionDataStore.instance = new SessionDataStore()
		}
		return SessionDataStore.instance
	}

	public isAuthenticated() {
		return this.token !== ""
	}

	public setToken(token: string) {
		this.token = token
	}

	public setFirstName(firstName: string) {
		this.firstName = firstName
	}

	public setLastName(lastName: string) {
		this.lastName = lastName
	}

	public setRoles(roles: Array<string>) {
		this.roles = roles
	}

	public setLastAcceptedTerms(lastAcceptedTerms: string) {
		this.lastAcceptedTerms = lastAcceptedTerms
	}

	public setMedalliaLastUpdate(medalliaHistory: string) {
		this.medalliaLastUpdate = medalliaHistory
	}

	public setFeatures(features: FeatureProperties) {
		this.features = features
	}

	public setPlans(plans: Array<string>) {
		this.plans = plans
	}

	public getFeatures() {
		return this.features
	}

	public getToken() {
		return this.token
	}

	public getEmail() {
		return this.email
	}

	public getRoles() {
		return this.roles
	}

	public getIsSupplier() {
		return this.isSupplier
	}

	public getUsername() {
		if (!this.isAuthenticated()) {
			return ""
		}
		return String(`${this.firstName} ${this.lastName}`)
	}

	public getUserInitials() {
		if (!this.isAuthenticated()) {
			return ""
		}
		return String(`${this.firstName.charAt(0)}${this.lastName.charAt(0)}`)
	}

	public getLastAcceptedTerms() {
		return this.lastAcceptedTerms
	}

	public getMedalliaLastUpdate() {
		return this.medalliaLastUpdate
	}

	public getPlans() {
		return this.plans
	}

	public getOrganizationName() {
		return this.organizationName
	}

	public save(authResponse: AuthResponse) {
		this.token = authResponse.token
		this.firstName = authResponse.firstName
		this.lastName = authResponse.lastName
		this.email = authResponse.email
		this.roles = authResponse.roles
		this.isSupplier = authResponse.isSupplier
		this.lastAcceptedTerms = authResponse.lastAcceptedTerms
		this.plans = authResponse.plans
		this.organizationName = authResponse.organizationName
		this.medalliaLastUpdate = authResponse.medalliaLastUpdate
	}

	public clear() {
		this.token = ""
		this.firstName = ""
		this.lastName = ""
		this.email = ""
		this.roles = []
		this.isSupplier = false
		this.lastAcceptedTerms = ""
		this.organizationName = ""
		this.plans = []
	}
}
